import type { FinishResult } from '../core/session/useGameSession';
import { PassatempoDetail, getGabarito, getPassatempo, getProgresso } from './api';
import type { iGameSaved, iGameTemplate } from './context/interfaces';
import {
  countCorrectCrossword,
  isVerifiedCache,
  maxCrosswordScore,
  mergeSavedMatrix,
  scoreCrossword,
} from './rules';

export interface CrosswordLoad {
  detail: PassatempoDetail;
  template: iGameTemplate;
  savedGame: iGameSaved;
  savedSeconds: number;
  finished: FinishResult | null;
}

const DIRECT_STYLE = 5;

export function isDirectCrossword(detail: PassatempoDetail): boolean {
  return (detail.estilos_de_passatempos_id ?? detail.estilosDePassatemposId) === DIRECT_STYLE;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Abre a cruzada a partir do que está salvo. NUNCA grava nada: sem progresso,
 * começa em branco; se a leitura do progresso falhar, o erro sobe (a tela não
 * abre em branco para não sobrescrever o progresso no autosave).
 */
export async function loadCrossword(id: number): Promise<CrosswordLoad> {
  const [detail, template, record] = await Promise.all([getPassatempo(id), getGabarito(id), getProgresso(id)]);
  const cache: Record<string, unknown> | null = record && isRecord(record.cache) ? record.cache : null;
  const matrix = mergeSavedMatrix(template, cache?.matrix);
  const tempo = cache?.tempo;
  const savedSeconds = typeof tempo === 'number' ? Math.max(0, Math.round(tempo)) : 0;
  const maxScore = maxCrosswordScore(template);
  const finished: FinishResult | null = isVerifiedCache(cache)
    ? { points: cache.acertos, maxPoints: cache.total, correct: countCorrectCrossword(template, matrix), total: maxScore }
    : null;

  const savedGame: iGameSaved = {
    id: record?.id ?? 0,
    passatempos_id: id,
    users_id: record?.usersId ?? 0,
    cache: { tempo: savedSeconds, pontuacao: scoreCrossword(template, matrix), matrix },
  };
  return { detail, template, savedGame, savedSeconds, finished };
}
